import React, { useState, useEffect } from 'react';

/**
 * PasswordProtection Component
 * 
 * Environment Variables:
 * - VITE_PASSWORD_PROTECT: Set to 'TRUE' to enable password protection
 * - VITE_PASSWORD: The password required for authentication (default: 'dev123')
 * - VITE_LOGIN_TITLE: Title displayed on login page (default: 'Hairmedics')
 * - VITE_LOGIN_SUBTITLE: Subtitle/description text (default: 'Voer uw wachtwoord in om verder te gaan')
 * - VITE_LOGIN_PASSWORD_PLACEHOLDER: Placeholder text for password input (default: 'Voer wachtwoord in')
 * - VITE_LOGIN_BUTTON_TEXT: Text displayed on login button (default: 'Inloggen')
 * - VITE_LOGIN_ERROR_MESSAGE: Error message for incorrect password (default: 'Onjuist wachtwoord')
 */

interface PasswordProtectionProps {
  children: React.ReactNode;
}

const PasswordProtection: React.FC<PasswordProtectionProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isProtectionEnabled, setIsProtectionEnabled] = useState(false);

  useEffect(() => {
    // Check if password protection is enabled via environment variable
    const isEnabled = import.meta.env.VITE_PASSWORD_PROTECT === 'TRUE';
    setIsProtectionEnabled(isEnabled);
    
    if (!isEnabled) {
      setIsAuthenticated(true);
      return;
    }

    // Check if user is already authenticated
    const authStatus = localStorage.getItem('hairmedics_auth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const correctPassword = import.meta.env.VITE_PASSWORD || 'dev123';
    
    if (password === correctPassword) {
      localStorage.setItem('hairmedics_auth', 'true');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError(import.meta.env.VITE_LOGIN_ERROR_MESSAGE || 'Onjuist wachtwoord');
    }
  };

  // If protection is disabled or user is authenticated, show the app
  if (!isProtectionEnabled || isAuthenticated) {
    return <>{children}</>;
  }

  // Get dynamic content from environment variables
  const loginTitle = import.meta.env.VITE_LOGIN_TITLE || 'Hairmedics';
  const loginSubtitle = import.meta.env.VITE_LOGIN_SUBTITLE || 'Voer uw wachtwoord in om verder te gaan';
  const passwordPlaceholder = import.meta.env.VITE_LOGIN_PASSWORD_PLACEHOLDER || 'Voer wachtwoord in';
  const loginButtonText = import.meta.env.VITE_LOGIN_BUTTON_TEXT || 'Inloggen';

  // Show login form with website theme styling
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--color-white)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontFamily: 'var(--font-body)',
      padding: 'var(--spacing-md)'
    }}>
      <div style={{
        backgroundColor: 'var(--color-white)',
        padding: 'var(--spacing-3xl)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)',
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center',
        border: '1px solid var(--color-light-grey)'
      }}>
        <div style={{
          marginBottom: 'var(--spacing-xl)',
          fontFamily: 'var(--h2-font-family)',
          fontWeight: 'var(--h2-font-weight)',
          fontSize: 'var(--h2-font-size)',
          lineHeight: 'var(--h2-line-height)',
          color: 'var(--color-dark-blue-token)'
        }}>
          {loginTitle}
        </div>
        
        {loginSubtitle && (
          <div style={{
            marginBottom: 'var(--spacing-xl)',
            fontFamily: 'var(--paragraph-m-font-family)',
            fontWeight: 'var(--paragraph-m-font-weight)',
            fontSize: 'var(--paragraph-m-font-size)',
            lineHeight: 'var(--paragraph-m-line-height)',
            color: 'var(--color-grey)'
          }}>
            {loginSubtitle}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <input
              type="password"
              placeholder={passwordPlaceholder}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '16px 24px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--paragraph-m-font-size)',
                fontFamily: 'var(--paragraph-m-font-family)',
                boxSizing: 'border-box',
                transition: 'var(--transition-base)',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--color-primary)';
                e.target.style.boxShadow = '0 0 0 3px rgba(36, 94, 124, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--color-light-grey)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          <button
            type="submit"
            style={{
              width: '100%',
              padding: '16px 24px',
              backgroundColor: 'var(--color-dark-blue-token)',
              color: 'var(--color-white)',
              border: '1px solid var(--color-dark-blue-token)',
              borderRadius: 'var(--radius-full)',
              fontSize: 'var(--paragraph-m-font-size)',
              fontFamily: 'var(--paragraph-m-font-family)',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'var(--transition-base)',
              minHeight: '54px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-white)';
              e.currentTarget.style.color = 'var(--color-dark-blue-token)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-dark-blue-token)';
              e.currentTarget.style.color = 'var(--color-white)';
            }}
          >
            {loginButtonText}
          </button>
          
          {error && (
            <div style={{
              color: '#e74c3c',
              marginTop: 'var(--spacing-lg)',
              fontFamily: 'var(--paragraph-s-font-family)',
              fontSize: 'var(--paragraph-s-font-size)',
              fontWeight: 'var(--paragraph-s-font-weight)'
            }}>
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PasswordProtection;